import { ReactNode } from 'react';
import { GetStaticProps } from 'next';
import { formatDateTime, PageViewTracker } from '@belong/common';
import { Text, Image } from '@belong/ui';
import { Footer } from 'src/components/footer/footer';
import clsx from 'clsx';
import { Header } from 'src/components/header/header';
import { MetaSEO } from 'src/components/meta-seo/meta-seo';
import { RichTextSlice } from 'src/features/blog/components/rich-text-slice/rich-text-slice';
import { fetchTermsOfUse, TermsOfUseResponse } from 'src/features/cms/prismic';
import { SeoHint } from 'src/features/home-page/components/seo-hint/seo-hint';
import css from 'src/features/terms-and-conditions/terms-and-conditions.module.css';
import { TERMS_AND_CONDITIONS_STRINGS } from 'src/features/terms-and-conditions/terms-and-conditions.strings';

type TermsAndConditionsProps = TermsOfUseResponse;

export default function TermsAndConditions({
  updated,
  content,
}: TermsAndConditionsProps) {
  return (
    <PageViewTracker name="terms">
      <MetaSEO
        title={TERMS_AND_CONDITIONS_STRINGS['meta.title']}
        description={TERMS_AND_CONDITIONS_STRINGS['meta.description']}
        openGraph={{
          title: TERMS_AND_CONDITIONS_STRINGS['meta.title'],
          description: TERMS_AND_CONDITIONS_STRINGS['meta.description'],
        }}
      />
      <div className={clsx(css.grid, 'relative overflow-hidden')}>
        <div className={clsx(css.gradient)} />
        <SeoHint />
        <div className="isolate">
          <div className="xl:max-w-container-xl relative w-full lg:container">
            <Image
              src="/home-page/cloud.png"
              alt=""
              className={clsx(css.leftCloud)}
            />
            <Image
              src="/home-page/cloud.png"
              alt=""
              className={clsx(css.leftSmallCloud)}
            />
            <Image
              src="/home-page/balloon.svg"
              alt=""
              className={clsx(css.balloon)}
            />
            <Image
              src="/home-page/cloud.png"
              alt=""
              className={clsx(css.rightCloud)}
            />
            <Image src="/home-page/sun.png" alt="" className={clsx(css.sun)} />
            <div className="mb-5xl gap-sm pt-3xl md:pt-4xl flex flex-col">
              <Text
                as="h1"
                variant="h1"
                fontWeight="semibold"
                className="text-center"
              >
                {TERMS_AND_CONDITIONS_STRINGS['page.title']}
              </Text>
              <Text as="h2" className="text-center">
                {`${TERMS_AND_CONDITIONS_STRINGS['page.updated']}${formatDateTime(
                  {
                    dateTime: updated,
                    format: 'MMMM d, yyyy',
                  },
                )}`}
              </Text>
            </div>
          </div>
        </div>
        <div className="pb-xl container flex justify-center">
          <div className={clsx(css.termsContainer, 'text-justify')}>
            <RichTextSlice primary={{ text: content }} />
          </div>
        </div>
      </div>
    </PageViewTracker>
  );
}

TermsAndConditions.getLayout = (page: ReactNode): ReactNode => {
  return (
    <div className="relative flex min-h-full flex-col overflow-x-hidden">
      <div className="relative z-10">
        <Header variant="tertiary" />
      </div>
      <main className="flex-1">{page}</main>
      <Footer />
    </div>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  const { data } = await fetchTermsOfUse();
  const { updated, content } = data;

  return {
    props: { updated, content },
    revalidate: 60,
  };
};
